// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { ForwardedRef } from 'react';
import { forwardRef, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import type { Store } from '@jane/shared/types';

import { useKioskSpacing, useScrollDirection } from '../../../hooks';
import type { MenuCategory as TMenuCategory } from '../../../types';
import { horizontallyScrollable, transitionStyles } from '../../../util';
import { Grid } from '../Grid/Grid';
import { ThemedBackground } from '../ThemedBackground';
import { MenuCategory } from './MenuCategory';
import { categories } from './categories';

const duration = 250;

interface MenuCategoriesProps {
  currentCategory: TMenuCategory;
  handleCategorySelected(category: TMenuCategory): void;
  isJaneMode?: boolean;
  store: Store;
  visibleTabs: TMenuCategory[];
}

const Container = styled(Grid)(() => {
  const { getKioskSpacing } = useKioskSpacing();
  const px = getKioskSpacing(64, 48);

  return {
    ...horizontallyScrollable,
    paddingLeft: px,
    paddingRight: px,
    paddingTop: '24px',
  };
});

export const MenuCategories = forwardRef(
  (
    {
      currentCategory,
      handleCategorySelected,
      isJaneMode,
      store,
      visibleTabs,
    }: MenuCategoriesProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const nodeRef = useRef(null);
    const scrollDirection = useScrollDirection(150);
    const { getKioskSpacing } = useKioskSpacing();

    const CategoryCards = useMemo(
      () =>
        visibleTabs.map((category) => {
          const categoryToRender =
            category === 'flower' && store.state === 'Ohio'
              ? 'alt-flower'
              : category;
          const meta = categories[categoryToRender];
          const selected = currentCategory === category;

          return (
            <MenuCategory
              category={category}
              isJaneMode={isJaneMode}
              handleCategorySelected={handleCategorySelected}
              key={`${meta.title}`}
              meta={meta}
              selected={selected}
              store={store}
            />
          );
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        currentCategory,
        handleCategorySelected,
        isJaneMode,
        store.id,
        visibleTabs,
      ]
    );

    return (
      <CSSTransition
        in={scrollDirection !== 'down'}
        nodeRef={nodeRef}
        timeout={duration}
      >
        {(state) => (
          <ThemedBackground
            ref={nodeRef}
            style={{
              marginTop: '-8px',
              paddingBottom: 32,
              position: 'relative',
              pointerEvents: state === 'exited' ? 'none' : 'auto',
              transition: `transform ${duration}ms ease-in-out`,
              zIndex: 1,
              ...transitionStyles({
                entered: { transform: `translateY(0)` },
                exited: { transform: `translateY(-100%)` },
              })(state),
            }}
          >
            <Container
              ref={ref}
              gap={getKioskSpacing(48, 40)}
              gridTemplateColumns={`repeat(${visibleTabs.length}, 1fr)`}
            >
              {CategoryCards}
            </Container>
          </ThemedBackground>
        )}
      </CSSTransition>
    );
  }
);
